/**
 * DecisionsSummary.js — Montags-Cockpit
 * Alle Notizen der gewählten Kampagne(n), scrollbar, Status direkt änderbar
 * v1.0 | 2026-03-06
 */

import React, { useState, useEffect, useCallback } from 'react';
import './DecisionsSummary.css';

const API = '/api/ads_decisions.php';

const STATUS_LABELS = {
    offen:      { label: 'Offen',     cls: 'ds-status-offen' },
    umgesetzt:  { label: 'Umgesetzt', cls: 'ds-status-umgesetzt' },
    geprueft:   { label: 'Geprüft',   cls: 'ds-status-geprueft' },
};

const STATUS_NEXT = {
    offen:     'umgesetzt',
    umgesetzt: 'geprueft',
    geprueft:  'offen',
};

const BEREICH_LABELS = {
    campaigns:       '📊 Kampagne',
    devices:         '📱 Geräte',
    schedule:        '🕐 Werbezeitplan',
    age:             '👥 Alter',
    keywords:        '🔑 Keywords',
    assets:          '🖼️ Assets',
    assets_titles:   '🖼️ Titel',
    assets_texts:    '🖼️ Texte',
    assets_sitelinks:'🖼️ Sitelinks',
    journey:         '👣 Journey',
    auktionen:       '⚡ Auktionen',
    standorte:       '📍 Standorte',
    zielgruppen:     '🎯 Zielgruppen',
    suchbegriffe:    '🔍 Suchbegriffe',
};

export default function DecisionsSummary({ selectedIds, campaignList }) {
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [filter, setFilter]     = useState('offen');   // offen | umgesetzt | geprueft | alle
    const [updating, setUpdating] = useState(null);
    const token = localStorage.getItem('token') || '';

    const load = useCallback(async () => {
        if (!selectedIds || selectedIds.length === 0) { setData([]); return; }
        setLoading(true);
        try {
            const url = `${API}?campaign_ids=${selectedIds.join(',')}&token=${token}`;
            const res = await fetch(url);
            const json = await res.json();
            if (json.success) setData(json.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [selectedIds, token]);

    useEffect(() => { load(); }, [load]);

    async function toggleStatus(item) {
        const nextStatus = STATUS_NEXT[item.status] || 'offen';
        setUpdating(item.id);
        try {
            await fetch(API, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.id, status: nextStatus }),
            });
            setData(prev => prev.map(d => d.id === item.id ? { ...d, status: nextStatus } : d));
        } catch (e) { console.error(e); }
        setUpdating(null);
    }

    // Filtern
    const filtered = filter === 'alle' ? data : data.filter(d => d.status === filter);

    // Nach Bereich gruppieren
    const grouped = {};
    filtered.forEach(d => {
        const b = d.bereich || 'sonstige';
        if (!grouped[b]) grouped[b] = [];
        grouped[b].push(d);
    });

    const total  = data.length;
    const offen  = data.filter(d => d.status === 'offen').length;
    const done   = data.filter(d => d.status === 'umgesetzt' || d.status === 'geprueft').length;

    // Kampagnenname für Anzeige
    const campNames = selectedIds.map(id => {
        const c = campaignList.find(x => String(x.campaign_id) === String(id));
        return c ? c.campaign_name : id;
    }).join(', ');

    return (
        <div className="ds-wrap">
            {/* Header */}
            <div className="ds-header">
                <div className="ds-title-row">
                    <span className="ds-title">📋 Aktionsplan</span>
                    <span className="ds-campaign">{campNames}</span>
                </div>
                <div className="ds-meta-row">
                    <span className="ds-count ds-count-offen">{offen} offen</span>
                    <span className="ds-count ds-count-done">{done} erledigt</span>
                    <span className="ds-count">{total} gesamt</span>
                    <div className="ds-filter-btns">
                        {['offen','umgesetzt','geprueft','alle'].map(f => (
                            <button
                                key={f}
                                className={"ds-filter-btn" + (filter === f ? ' active' : '')}
                                onClick={() => setFilter(f)}
                            >
                                {f === 'alle' ? 'Alle' : STATUS_LABELS[f]?.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Scrollfenster */}
            <div className="ds-scroll">
                {loading && <p className="ds-empty">Lädt…</p>}
                {!loading && filtered.length === 0 && (
                    <p className="ds-empty">
                        {filter === 'offen' ? '✅ Alle Punkte erledigt.' : 'Keine Einträge.'}
                    </p>
                )}
                {!loading && Object.entries(grouped).map(([bereich, items]) => (
                    <div key={bereich} className="ds-group">
                        <div className="ds-group-label">
                            {BEREICH_LABELS[bereich] || bereich}
                            <span className="ds-group-count">{items.length}</span>
                        </div>
                        {items.map(item => (
                            <div key={item.id} className={"ds-item ds-item-" + item.status}>
                                <div className="ds-item-body">
                                    <div className="ds-item-beob">{item.beobachtung}</div>
                                    {item.massnahme && (
                                        <div className="ds-item-mass">→ {item.massnahme}</div>
                                    )}
                                </div>
                                <button
                                    className={"ds-status-btn " + (STATUS_LABELS[item.status]?.cls || '')}
                                    onClick={() => toggleStatus(item)}
                                    disabled={updating === item.id}
                                    title="Klick zum Weiterschalten"
                                >
                                    {updating === item.id ? '…' : STATUS_LABELS[item.status]?.label}
                                </button>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}